import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Job } from "../../models/Job";
import { fetchJobs, checkBackendStatus } from "../../services/jobService";
import { Filters } from "../../components/jobs/filtering/JobFilter";
import { LoginPromptDialog, SheetLoading } from "../../components/shared/ui";
import { useAuth } from "../../provider/AuthProvider";
import { useLoginModal } from "../../context/LoginModalContext";
import { fetchRecommendations } from "../../services/matchingService";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import { AcaIcon } from "../../components/shared/aca";
import SheetGround from "../../components/shared/ui/SheetGround";
import { VELLUM as T, SANS, MONO, display, mono, body, btn } from "../../components/shared/folio/kit";
import { FolioSelect } from "../../components/shared/folio/Select";
import { SUBJECT_CATEGORIES, SUBJECTS } from "../../data/academicTaxonomy";

const SORT_OPTIONS = [
  { value: "relevance", label: "Relevance" },
  { value: "date", label: "Newest" },
  { value: "salary", label: "Salary" },
];

const PER_PAGE_OPTIONS = ["10", "20", "50"];

/* The folio vocabulary under the token names this page already used.
   Operate surface: the sheet supplies the material, the task still leads. */
const pal = {
  paper: T.ground,
  /* The sheet has one ground; a second paper tone would be a material the
     folio does not have. Separation is done by rule instead. */
  paperDeep: T.ground,
  card: T.ground,
  ink: T.ink,
  inkSoft: T.mute,
  inkMute: T.mute,
  rule: T.rule,
  ruleSoft: T.rule,
  accent: T.accent,
  onAccent: T.onAccent,
};
const fam = { sans: SANS, mono: MONO, serif: SANS };

const EXPERIENCE_OPTIONS = ["1-2 Years", "3-5 Years", "6-9 Years", "10+ Years"] as const;
const JOB_TYPE_OPTIONS = ["Full Time", "Part Time", "Contract"] as const;
const LOCATION_OPTIONS = ["Remote", "Hybrid", "On Site"] as const;
const CONTRACT_OPTIONS = [
  "Permanent",
  "Fixed 1 Year",
  "Fixed 2 Years",
  "Fixed 3 Years",
  "Fixed 4 Years",
] as const;
const CAREER_OPTIONS = ["Associate Fellow", "Fellow", "Senior Fellow"] as const;

// Map subject name -> its discipline (subject category) for client-side derivation.
const SUBJECT_TO_CATEGORY = new Map<string, string>(
  SUBJECTS.map((s) => [s.name, s.category]),
);

function jobDisciplines(job: Job): string[] {
  // Prefer the explicit field; otherwise derive from subjects[] via the canonical taxonomy.
  if (job.subjectCategory) return [job.subjectCategory];
  const subs = job.subjects || [];
  const cats = new Set<string>();
  for (const s of subs) {
    const cat = SUBJECT_TO_CATEGORY.get(s);
    if (cat) cats.add(cat);
  }
  return Array.from(cats);
}

function postedAgo(date?: string): string {
  if (!date) return "—";
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days === 1) return "1d";
  if (days < 7) return `${days}d`;
  const wks = Math.floor(days / 7);
  if (wks < 5) return `${wks}w`;
  return `${Math.floor(days / 30)}mo`;
}

/* ─── FilterGroup — collapsible editorial check group ─────────────── */
const FilterGroup: React.FC<{
  label: string;
  options: readonly string[];
  picked: string[];
  onToggle: (v: string) => void;
  defaultOpen?: boolean;
}> = ({ label, options, picked, onToggle, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen || picked.length > 0);
  return (
    <div style={{ borderBottom: `1px solid ${pal.rule}`, padding: "20px 0" }}>
      {/* The group heading is the control. It reads in full ink at display
          weight so it is obviously pressable, and the mark is set to match
          rather than trail behind it in a lighter grey. */}
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="jb-fgroup"
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: pal.ink,
        }}
      >
        <span
          className="jb-fgroup-label"
          style={{
            fontFamily: fam.mono,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: pal.ink,
          }}
        >
          {label}
        </span>
        <span
          aria-hidden
          className="jb-fgroup-mark"
          style={{
            fontFamily: fam.mono,
            fontSize: 16,
            fontWeight: 700,
            lineHeight: 1,
            color: pal.ink,
            /* Fixed width so the heading does not shift as it opens. */
            width: 14,
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          {options.map((o) => {
            const checked = picked.includes(o);
            return (
              <label
                key={o}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontFamily: fam.sans,
                  fontSize: 13.5,
                  color: pal.ink,
                  cursor: "pointer",
                }}
              >
                <span
                  onClick={() => onToggle(o)}
                  style={{
                    width: 14,
                    height: 14,
                    border: `1.5px solid ${pal.ink}`,
                    flexShrink: 0,
                    background: checked ? pal.ink : "transparent",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {checked && <AcaIcon name="check" size={10} stroke={pal.paper} />}
                </span>
                {o}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Jobs: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { openLoginModal } = useLoginModal();
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [sortBy, setSortBy] = useState<"relevance" | "date" | "salary">("relevance");
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState(20);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    experience: [],
    categories: [],
    jobTypes: [],
    careerLevels: [],
    subjectCategories: [],
    studyLevels: [],
    locations: [],
    contractDurations: [],
    maxSalary: 500000,
  });
  const [matchScoresMap, setMatchScoresMap] = useState<Map<string, number>>(new Map());
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Guests should never see the listings page — bounce them home with the login modal open.
  // Wait until auth resolves before deciding to redirect.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(ROUTE_NAMES.PAGES.HOME, { replace: true });
      openLoginModal();
    }
  }, [user, authLoading, navigate, openLoginModal]);

  const toggleFilter = useCallback(
    (key: keyof Omit<Filters, "maxSalary">, value: string) => {
      setFilters((prev) => ({
        ...prev,
        [key]: prev[key].includes(value)
          ? prev[key].filter((v) => v !== value)
          : [...prev[key], value],
      }));
      setCurrentPage(1);
    },
    [],
  );

  const activeFilterCount =
    filters.experience.length +
    filters.jobTypes.length +
    filters.locations.length +
    filters.contractDurations.length +
    filters.careerLevels.length +
    filters.subjectCategories.length +
    (filters.maxSalary < 500000 ? 1 : 0);

  const clearAllFilters = () => {
    setFilters({
      experience: [],
      categories: [],
      jobTypes: [],
      careerLevels: [],
      subjectCategories: [],
      studyLevels: [],
      locations: [],
      contractDurations: [],
      maxSalary: 500000,
    });
    setSearchQuery("");
    setLocationQuery("");
    setSearchInput("");
    setLocationInput("");
    setCurrentPage(1);
  };

  const saveScrollPosition = () => {
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    sessionStorage.setItem("jobsScrollPosition", scrollPosition.toString());
    sessionStorage.setItem("jobsCurrentPage", currentPage.toString());
    sessionStorage.setItem("jobsPerPage", jobsPerPage.toString());
  };

  useEffect(() => {
    let scrollTimeoutId: ReturnType<typeof setTimeout> | null = null;
    const load = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const isConnected = await checkBackendStatus();
        if (!isConnected) throw new Error("The register could not be reached.");
        const fetched = await fetchJobs();
        setAllJobs(fetched);
        const limited = user ? fetched : fetched.slice(0, 10);
        setJobs(limited);

        const savedScroll = sessionStorage.getItem("jobsScrollPosition");
        const savedPage = sessionStorage.getItem("jobsCurrentPage");
        const savedPerPage = sessionStorage.getItem("jobsPerPage");
        if (savedPage) setCurrentPage(parseInt(savedPage, 10));
        if (savedPerPage) setJobsPerPage(parseInt(savedPerPage, 10));
        if (savedScroll) {
          scrollTimeoutId = setTimeout(() => window.scrollTo(0, parseInt(savedScroll, 10)), 100);
        }
        sessionStorage.removeItem("jobsScrollPosition");
        sessionStorage.removeItem("jobsCurrentPage");
        sessionStorage.removeItem("jobsPerPage");
      } catch (err) {
        console.error("Failed to load jobs:", err);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (scrollTimeoutId !== null) clearTimeout(scrollTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && user.role === "employee" && jobs.length > 0) {
      fetchRecommendations(1, 50)
        .then((data) => {
          const scores = new Map<string, number>();
          for (const rec of data.recommendations) scores.set(rec.job.id, rec.matchScore);
          setMatchScoresMap(scores);
        })
        .catch(() => {/* silent */});
    }
  }, [user, jobs.length]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get("q") || "";
    const loc = urlParams.get("location") || "";
    const subjectCategory = urlParams.get("subjectCategory");
    setSearchQuery(query);
    setLocationQuery(loc);
    setSearchInput(query);
    setLocationInput(loc);
    if (subjectCategory) {
      setFilters((prev) => ({ ...prev, subjectCategories: [subjectCategory] }));
    }
  }, []);

  const allFilteredJobs = useMemo(
    () =>
      allJobs.filter((job) => {
        const q = searchQuery.toLowerCase();
        const matchesSearch =
          !searchQuery ||
          (job.title || '').toLowerCase().includes(q) ||
          (job.company || '').toLowerCase().includes(q) ||
          (job.description || '').toLowerCase().includes(q);
        const matchesLocation =
          !locationQuery || (job.location || '').toLowerCase().includes(locationQuery.toLowerCase());
        const matchExperience =
          filters.experience.length === 0 || filters.experience.includes(job.experience);
        const matchType =
          filters.jobTypes.length === 0 || filters.jobTypes.includes(job.employmentType);
        const matchCareer =
          filters.careerLevels.length === 0 || filters.careerLevels.includes(job.careerLevel);
        const matchLocation =
          filters.locations.length === 0 || filters.locations.includes(job.type);
        const matchContract =
          filters.contractDurations.length === 0 ||
          (!!job.contractDuration && filters.contractDurations.includes(job.contractDuration));
        const matchDiscipline =
          filters.subjectCategories.length === 0 ||
          jobDisciplines(job).some((c) => filters.subjectCategories.includes(c));
        const matchSalary = job.salaryValue <= filters.maxSalary;
        return (
          matchesSearch &&
          matchesLocation &&
          matchExperience &&
          matchType &&
          matchCareer &&
          matchLocation &&
          matchContract &&
          matchDiscipline &&
          matchSalary
        );
      }),
    [allJobs, searchQuery, locationQuery, filters],
  );

  const filteredJobs = useMemo(
    () => allFilteredJobs.slice(0, user ? allFilteredJobs.length : 10),
    [allFilteredJobs, user],
  );

  const sortedJobs = useMemo(
    () =>
      [...filteredJobs].sort((a, b) => {
        switch (sortBy) {
          case "date": {
            const dateA = a.postedDate ? new Date(a.postedDate).getTime() : 0;
            const dateB = b.postedDate ? new Date(b.postedDate).getTime() : 0;
            return (b.hot ? 1 : 0) - (a.hot ? 1 : 0) || dateB - dateA;
          }
          case "salary":
            return (b.hot ? 1 : 0) - (a.hot ? 1 : 0) || b.salaryValue - a.salaryValue;
          case "relevance":
          default:
            if (matchScoresMap.size > 0) {
              const sa = matchScoresMap.get(a.id) || 0;
              const sb = matchScoresMap.get(b.id) || 0;
              if (sb !== sa) return sb - sa;
            }
            return (b.hot ? 1 : 0) - (a.hot ? 1 : 0);
        }
      }),
    [filteredJobs, sortBy, matchScoresMap],
  );

  const totalPages = Math.max(1, Math.ceil(sortedJobs.length / jobsPerPage));
  const startIndex = (currentPage - 1) * jobsPerPage;
  const paginatedJobs = sortedJobs.slice(startIndex, startIndex + jobsPerPage);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setSearchQuery(searchInput.trim());
    setLocationQuery(locationInput.trim());
    setCurrentPage(1);
  };

  const handleJobClick = (id: string) => {
    if (!user) {
      openLoginModal();
      return;
    }
    saveScrollPosition();
    navigate(`${ROUTE_NAMES.PAGES.JOB_DETAILS}/${id}`);
  };

  // Guest redirect runs in the effect above — render nothing while it fires.
  if (!user) {
    return null;
  }

  if (loading) {
    return <SheetLoading text="Loading the register…" />;
  }

  const FilterPanel = (
    <div>
      <FilterGroup
        label="Discipline"
        options={SUBJECT_CATEGORIES}
        picked={filters.subjectCategories}
        onToggle={(v) => toggleFilter("subjectCategories", v)}
        defaultOpen={false}
      />
      <FilterGroup
        label="Job type"
        options={JOB_TYPE_OPTIONS}
        picked={filters.jobTypes}
        onToggle={(v) => toggleFilter("jobTypes", v)}
      />
      <FilterGroup
        label="Work model"
        options={LOCATION_OPTIONS}
        picked={filters.locations}
        onToggle={(v) => toggleFilter("locations", v)}
      />
      <FilterGroup
        label="Contract"
        options={CONTRACT_OPTIONS}
        picked={filters.contractDurations}
        onToggle={(v) => toggleFilter("contractDurations", v)}
        defaultOpen={false}
      />
      <FilterGroup
        label="Experience"
        options={EXPERIENCE_OPTIONS}
        picked={filters.experience}
        onToggle={(v) => toggleFilter("experience", v)}
      />
      <FilterGroup
        label="HE recognition"
        options={CAREER_OPTIONS}
        picked={filters.careerLevels}
        onToggle={(v) => toggleFilter("careerLevels", v)}
        defaultOpen={false}
      />

      <div style={{ borderBottom: `1px solid ${pal.rule}`, padding: "20px 0" }}>
        {/* Not collapsible, but it is a group heading like the others and
            is set to match them. */}
        <div
          style={{
            fontFamily: fam.mono,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: pal.ink,
            marginBottom: 14,
          }}
        >
          Max salary
        </div>
        <input
          type="range"
          min={20000}
          max={500000}
          step={5000}
          value={filters.maxSalary}
          onChange={(e) => setFilters((prev) => ({ ...prev, maxSalary: Number(e.target.value) }))}
          style={{ width: "100%", accentColor: pal.accent }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 6,
            fontFamily: fam.mono,
            fontSize: 11,
            color: pal.inkSoft,
          }}
        >
          <span>£20k</span>
          <span style={{ color: pal.ink }}>£{Math.round(filters.maxSalary / 1000)}k</span>
          <span>£500k+</span>
        </div>
      </div>

      {activeFilterCount > 0 && (
        <button
          onClick={clearAllFilters}
          style={{
            marginTop: 20,
            width: "100%",
            padding: "12px 14px",
            background: "transparent",
            border: `1px solid ${pal.ink}`,
            color: pal.ink,
            cursor: "pointer",
            fontFamily: fam.mono,
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Clear all ({activeFilterCount})
        </button>
      )}
    </div>
  );

  return (
    <SheetGround className="jb-root" style={{ color: T.ink, fontFamily: SANS, minHeight: "100vh" }}>
      {/* HERO + SEARCH */}
      <section style={{ borderBottom: `1px solid ${pal.rule}` }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "36px 20px 32px" }}>
          {/* The code sits on the heading's baseline, never stacked above
              it. The figure is the sheet's live measurement, so it is set in
              the accent and underscored rather than in a borrowed italic. */}
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
            <h1 style={{ ...display(T, "clamp(36px, 5.5vw, 64px)"), lineHeight: 0.96, letterSpacing: "-0.04em" }}>
              Browse{" "}
              <span style={{ color: pal.accent, borderBottom: `3px solid ${pal.accent}` }}>
                {allFilteredJobs.length.toLocaleString()}
              </span>{" "}
              open positions.
            </h1>
            <span style={mono(T, T.accent)}>§ 01 · The register / open positions, live</span>
          </div>

          <form
            onSubmit={handleSearch}
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1.2fr auto",
              gap: 16,
              alignItems: "end",
              marginTop: 28,
            }}
            className="aca-jobs-search"
          >
            <div className="aca-jobs-field">
              <div
                style={{
                  fontFamily: fam.mono,
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: pal.inkSoft,
                  marginBottom: 6,
                }}
              >
                01 · Keyword
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  borderBottom: `2px solid ${pal.ink}`,
                  padding: "8px 0",
                }}
              >
                <AcaIcon name="search" size={18} />
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="title, supervisor, subject"
                  style={{
                    border: "none",
                    background: "transparent",
                    flex: 1,
                    padding: "0 10px",
                    fontFamily: fam.sans,
                    fontSize: 18,
                    color: pal.ink,
                    outline: "none",
                  }}
                />
              </div>
            </div>
            <div className="aca-jobs-field">
              <div
                style={{
                  fontFamily: fam.mono,
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: pal.inkSoft,
                  marginBottom: 6,
                }}
              >
                02 · Location
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  borderBottom: `2px solid ${pal.ink}`,
                  padding: "8px 0",
                }}
              >
                <AcaIcon name="pin" size={18} />
                <input
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  placeholder="anywhere in UK"
                  style={{
                    border: "none",
                    background: "transparent",
                    flex: 1,
                    padding: "0 10px",
                    fontFamily: fam.sans,
                    fontSize: 18,
                    color: pal.ink,
                    outline: "none",
                  }}
                />
              </div>
            </div>
            <button type="submit" className="jb-accent" style={{ ...btn(T, "accent"), justifyContent: "center" }}>Search</button>
          </form>
        </div>
      </section>

      {/* MAIN LAYOUT */}
      <section>
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "32px 20px 56px",
            display: "grid",
            gridTemplateColumns: "280px 1fr",
            gap: 40,
          }}
          className="aca-jobs-layout"
        >
          {/* Sidebar (desktop) */}
          <aside className="aca-jobs-sidebar">
            <div
              style={{
                fontFamily: fam.mono,
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: pal.inkSoft,
                borderBottom: `2px solid ${pal.ink}`,
                paddingBottom: 12,
                marginBottom: 6,
              }}
            >
              § Filters
            </div>
            {FilterPanel}
          </aside>

          <div>
            {/* Sort / mobile filters */}
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                borderBottom: `1px solid ${pal.rule}`,
                paddingBottom: 14,
                marginBottom: 0,
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: fam.mono,
                    fontSize: 11,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: pal.inkSoft,
                  }}
                >
                  § Listings
                </div>
                <div
                  style={{
                    fontFamily: fam.sans,
                    fontSize: 22,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                    color: pal.ink,
                  }}
                >
                  {sortedJobs.length.toLocaleString()} posts{" "}
                  <span style={{ color: pal.inkSoft, fontWeight: 400, fontFamily: fam.serif, fontSize: 18 }}>
                    — page {currentPage} of {totalPages}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 14, alignItems: "center", fontFamily: fam.mono, fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: pal.inkSoft }}>
                <button
                  type="button"
                  onClick={() => setShowMobileFilters(true)}
                  className="aca-mobile-filters-btn"
                  style={{
                    display: "none",
                    background: "transparent",
                    border: `1px solid ${pal.ink}`,
                    color: pal.ink,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontFamily: fam.mono,
                    fontSize: 10.5,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                  }}
                >
                  Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                </button>
                <span>Sort</span>
                <FolioSelect
                  size="sm"
                  tone="mono"
                  width={132}
                  ariaLabel="Sort the register by"
                  value={sortBy}
                  onChange={(v) => setSortBy(v as typeof sortBy)}
                  options={SORT_OPTIONS}
                />
                <span>Per page</span>
                <FolioSelect
                  size="sm"
                  tone="mono"
                  width={84}
                  align="right"
                  ariaLabel="Entries per page"
                  value={String(jobsPerPage)}
                  onChange={(v) => { setJobsPerPage(Number(v)); setCurrentPage(1); }}
                  options={PER_PAGE_OPTIONS}
                />
              </div>
            </div>

            {/* The drawer head. Two tracks only: what the post is, and
                what it is filed as. */}
            <div
              className="jb-thead"
              style={{
                ...mono(T),
                display: "grid",
                gridTemplateColumns: "54px minmax(0,1fr) 172px",
                gap: 20,
                padding: "16px 0 10px",
                borderBottom: `1.5px solid ${pal.ink}`,
              }}
            >
              <div />
              <div>Position</div>
              <div style={{ textAlign: "right" }}>Particulars</div>
            </div>

            {/* A fault and an empty result are different facts and must not
                read the same. The register only claims that nothing matches
                once it has actually reached the register. */}
            {loadFailed ? (
              <div
                role="alert"
                style={{ padding: "56px 0", textAlign: "center", borderBottom: `1px solid ${pal.rule}` }}
              >
                <div className="jb-empty-mark" aria-hidden />
                <p style={{ ...display(T, "clamp(20px, 2.6vw, 26px)") }}>
                  The register could not be reached.
                </p>
                <p style={{ ...body(T), color: pal.inkSoft, marginTop: 10 }}>
                  This is a fault on our side, not an empty result. Your filters are untouched.
                </p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{ ...btn(T, "outline"), marginTop: 22 }}
                >
                  Try again
                </button>
              </div>
            ) : paginatedJobs.length === 0 ? (
              <div style={{ padding: "56px 0", textAlign: "center", borderBottom: `1px solid ${pal.rule}` }}>
                <div className="jb-empty-mark" aria-hidden />
                <p style={{ ...display(T, "clamp(20px, 2.6vw, 26px)") }}>
                  No positions match the current filters.
                </p>
              </div>
            ) : null}

            {/* Each post is an entry in the drawer, not a row in a
                spreadsheet: the title gets the measure it needs, and the
                facts it is filed under sit in the margin of its own card.
                A button rather than a clickable div, so the keyboard can
                reach it - and the handler still saves scroll position. */}
            {paginatedJobs.map((j, i) => (
              <button
                key={j.id}
                type="button"
                onClick={() => handleJobClick(j.id)}
                className="jb-entry"
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "54px minmax(0,1fr) 172px",
                  gap: 20,
                  alignItems: "start",
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  borderBottom: `1px solid ${pal.rule}`,
                  borderRadius: 0,
                  padding: "22px 0",
                  cursor: "pointer",
                  color: pal.ink,
                }}
              >
                {/* The register number runs on across pages, so an entry can
                    be cited rather than only clicked. */}
                <span style={{ ...mono(T, T.accent), display: "inline-flex", alignItems: "center", gap: 7, paddingTop: 5 }}>
                  <span aria-hidden style={{ width: 9, height: 1, background: pal.accent }} />
                  {String((currentPage - 1) * jobsPerPage + i + 1).padStart(2, "0")}
                </span>

                <span style={{ minWidth: 0 }}>
                  <span
                    className="jb-entry-title"
                    style={{ ...display(T, "clamp(18px, 2.1vw, 22px)"), display: "block", lineHeight: 1.2 }}
                  >
                    {j.title}
                    {j.hot && <span className="jb-hot">Hot</span>}
                  </span>
                  <span style={{ ...body(T), fontSize: 14.5, display: "block", marginTop: 7 }}>
                    {j.company}
                    {j.location ? ` · ${j.location}` : ""}
                  </span>
                  {j.subjects && j.subjects.length > 0 && (
                    <span style={{ ...mono(T), display: "flex", gap: 12, flexWrap: "wrap", marginTop: 9, letterSpacing: "0.08em" }}>
                      {j.subjects.slice(0, 3).map((sub) => (
                        <span key={sub}>#{sub.toLowerCase()}</span>
                      ))}
                    </span>
                  )}
                </span>

                <span className="jb-particulars" style={{ display: "grid", gap: 5, justifyItems: "end", textAlign: "right" }}>
                  <span style={mono(T)}>{j.employmentType || j.type}</span>
                  <span style={{ fontFamily: MONO, fontSize: 15, color: pal.ink }}>{j.salary}</span>
                  <span style={mono(T)}>posted {postedAgo(j.postedDate)}</span>
                  <span
                    className="jb-read"
                    style={{
                      ...mono(T, T.ink),
                      display: "inline-flex",
                      gap: 8,
                      alignItems: "center",
                      borderBottom: `1px solid ${pal.ink}`,
                      paddingBottom: 3,
                      marginTop: 8,
                    }}
                  >
                    Read <span style={{ fontSize: 12 }}>→</span>
                  </span>
                </span>
              </button>
            ))}

            {/* Login wall for guests with more results */}
            {!user && allFilteredJobs.length > 10 && (
              <div
                style={{
                  margin: "40px 0",
                  padding: "32px 28px",
                  border: `1px solid ${pal.ink}`,
                  background: pal.paperDeep,
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 24,
                  alignItems: "center",
                }}
                className="aca-login-wall"
              >
                <div>
                  <div
                    style={{
                      fontFamily: fam.mono,
                      fontSize: 10.5,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      color: pal.accent,
                    }}
                  >
                    § Members
                  </div>
                  <div
                    style={{
                      fontFamily: fam.sans,
                      fontSize: 22,
                      fontWeight: 600,
                      letterSpacing: "-0.02em",
                      marginTop: 6,
                    }}
                  >
                    {allFilteredJobs.length - 10}+ more positions in this filter.
                  </div>
                  <div
                    style={{
                      fontFamily: fam.sans,
                      color: pal.inkSoft,
                      fontSize: 15,
                      marginTop: 6,
                    }}
                  >
                    Sign in to browse the full register and save searches.
                  </div>
                </div>
                <button type="button" onClick={openLoginModal} className="jb-accent" style={btn(T, "accent")}>Sign in</button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  paddingTop: 32,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0,
                  flexWrap: "wrap",
                  fontFamily: fam.mono,
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={pagBtnStyle(currentPage === 1)}
                >
                  ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce<(number | "…")[]>((acc, p, i, arr) => {
                    if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("…");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "…" ? (
                      <span key={`e-${i}`} style={{ padding: "10px 12px", color: pal.inkSoft }}>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => {
                          setCurrentPage(p as number);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        style={{
                          ...pagBtnStyle(false),
                          background: currentPage === p ? pal.ink : "transparent",
                          color: currentPage === p ? pal.paper : pal.ink,
                          borderColor: currentPage === p ? pal.ink : pal.rule,
                        }}
                      >
                        {p}
                      </button>
                    ),
                  )}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={pagBtnStyle(currentPage === totalPages)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile filter drawer */}
      {showMobileFilters && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(12, 22, 20, 0.58)",
            zIndex: 100,
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "flex-end",
          }}
          onClick={() => setShowMobileFilters(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(360px, 100%)",
              background: pal.paper,
              borderLeft: `1px solid ${pal.rule}`,
              padding: "24px 22px",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  fontFamily: fam.mono,
                  fontSize: 11,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: pal.inkSoft,
                }}
              >
                § Filters
              </div>
              <button
                onClick={() => setShowMobileFilters(false)}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: pal.ink }}
                aria-label="Close"
              >
                <AcaIcon name="close" size={20} />
              </button>
            </div>
            {FilterPanel}
          </div>
        </div>
      )}

      <LoginPromptDialog isOpen={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} />

      <style>{`
        /* A group heading is a control: it answers to the pointer and to
           the keyboard, and the mark answers with it. */
        .jb-fgroup { transition: color 140ms ease; }
        .jb-fgroup:hover .jb-fgroup-label,
        .jb-fgroup:hover .jb-fgroup-mark { color: ${pal.accent}; }
        .jb-fgroup:focus-visible { outline: 2px solid ${pal.accent}; outline-offset: 4px; }

        /* An entry answers by inking, never by lifting. */
        .jb-entry { transition: border-color 160ms ease, transform 200ms cubic-bezier(0.16,1,0.3,1); transform-origin: left center; }
        .jb-entry:hover { transform: scale(1.004); border-bottom-color: ${pal.ink}; }
        .jb-entry:hover .jb-entry-title { color: ${pal.accent}; }
        .jb-entry:hover .jb-read { color: ${pal.accent}; border-bottom-color: ${pal.accent}; }
        .jb-entry-title, .jb-read { transition: color 140ms ease, border-color 140ms ease; }

        .jb-hot {
          display: inline-block;
          margin-left: 10px;
          vertical-align: 3px;
          font-family: ${MONO};
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: ${pal.onAccent};
          background: ${pal.accent};
          padding: 3px 7px;
        }

        .jb-empty-mark {
          width: 44px; height: 44px; margin: 0 auto 20px;
          border: 1.5px solid ${pal.rule}; position: relative;
        }
        .jb-empty-mark::before, .jb-empty-mark::after { content: ""; position: absolute; background: ${pal.rule}; }
        .jb-empty-mark::before { left: 9px; right: 9px; top: 50%; height: 1px; }
        .jb-empty-mark::after  { top: 9px; bottom: 9px; left: 50%; width: 1px; }

        .jb-accent { transition: background 150ms ease; }
        .jb-accent:hover { background: ${T.accentDeep}; }

        .jb-root input::placeholder { color: ${pal.inkSoft}; opacity: 1; }
        .jb-root button:focus-visible,
        .jb-root a:focus-visible,
        .jb-root input:focus-visible,
        .jb-root select:focus-visible {
          outline: 2px solid ${pal.accent}; outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          .jb-entry, .jb-entry-title, .jb-read, .jb-fgroup, .jb-accent { transition: none; }
          .jb-entry:hover { transform: none; }
        }

        @media (max-width: 1024px) {
          .aca-jobs-layout { grid-template-columns: 1fr !important; }
          .aca-jobs-sidebar { display: none !important; }
          .aca-mobile-filters-btn { display: inline-block !important; }
        }
        @media (max-width: 720px) {
          .aca-jobs-search { grid-template-columns: 1fr !important; }
          .jb-thead { display: none !important; }
          /* The entry stacks: number, then the reading, then the
             particulars left-aligned under it. */
          .jb-entry { grid-template-columns: minmax(0,1fr) !important; gap: 10px !important; padding: 20px 0 !important; }
          .jb-particulars { justify-items: start !important; text-align: left !important; }
          .aca-login-wall { grid-template-columns: 1fr !important; gap: 16px !important; }
        }
      `}</style>
    </SheetGround>
  );
};

const pagBtnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: "10px 14px",
  background: "transparent",
  border: `1px solid ${pal.rule}`,
  color: disabled ? pal.inkMute : pal.ink,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: fam.mono,
  fontSize: 11,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  marginLeft: -1,
  opacity: disabled ? 0.5 : 1,
});

export default Jobs;
